import apiClient from '@/services/api-client';
import store from '@/store/index';
import { apiSuccessCheck } from '../util/utilities';
import { isEmpty } from 'lodash';
import apiClientGet from '@/services/util/apiClientGet';
import apiClientPost from '@/services/util/apiClientPost';

/**
 * used for holding AbortControllers which can be used to cancel
 * api calls
 */
let controller = null;

export default {
	async exportContacts(payload) {
		try {
			const response = await apiClient().post(
				'/Export/ExportContacts',
				payload,
				{
					responseType: 'blob',
				},
			);

			if (
				Object.keys(response).length === 0 ||
				response.message === 'Network Error' ||
				!apiSuccessCheck(response.response ? response.response.status : 200)
			) {
				throw new Error('API call failed');
			}

			const file = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = file;
			link.setAttribute('download', 'contacts.csv');
			link.click();

			return Promise.resolve('success');
		} catch (error) {
			console.log('error', error);
			return Promise.reject(error);
		}
	},
	async getActivityLog(leadId) {
		try {
			const response = await apiClient().get(
				`/Lead/GetActivityLog?leadId=${leadId}`,
			);
			if (response && response.data) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getCreditWatchList(payload) {
		try {
			const response = await apiClient().post('/Lead/GetContactFico', payload);
			if (response) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getSearchLeads(payload) {
		if (controller) controller.abort();
		controller = new AbortController();
		try {
			const { status, data, message } = await apiClient().post(
				'/Lead/Search',
				payload,
				{
					signal: controller.signal,
				},
			);

			if (message === 'canceled') return;

			if (!apiSuccessCheck(status)) {
				throw new Error('API call failed');
			}
			if (isEmpty(data)) {
				throw new Error('No data received');
			} else {
				return Promise.resolve(data);
			}
		} catch (error) {
			if (error.message === 'canceled') {
				console.warn('API call was cancelled');
				return;
			}
			return Promise.reject(error);
		} finally {
			controller = null;
		}
	},
	async getPreferencesByLeadId(leadId) {
		try {
			const response = await apiClient().get(
				`/Preference/GetLeadPreferences?leadId=${leadId}`,
			);
			if (response && response.data) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getAllStates() {
		try {
			const response = await apiClient().get('/Lead/GetAllStates');
			if (response && response.data) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getAllLeadTypes() {
		try {
			const { status, data } = await apiClient().get('/Lead/GetAllLeadTypes');
			if (!apiSuccessCheck(status)) {
				throw new Error('API call failed');
			}
			if (isEmpty(data)) {
				throw new Error('No data received');
			} else {
				return Promise.resolve(data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getLeadDetailsById(leadId) {
		try {
			const data = await apiClientGet(`/Lead/GetLead?leadId=${leadId}`);
			return Promise.resolve(data);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async saveOwner(payload) {
		try {
			const response = await apiClient().post('/Lead/SaveOwner', payload);
			if (response) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			store.dispatch('showErrorModal', {
				error,
				errorDetails: ERROR_DETAILS.SAVE_OWNER,
			});
			return Promise.reject(error);
		}
	},
	async saveLeadDetails(payload) {
		try {
			const data = await apiClientPost('/Lead/SaveDetail', payload);
			return Promise.resolve(data);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async saveLeadPreferences(payload) {
		try {
			const response = await apiClient().post(
				'/Preference/SaveLeadPreferences',
				payload,
			);
			if (response && response.data) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async saveLeadNote(payload) {
		try {
			await apiClient().post('/Lead/InsertNote', payload);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async transferLeads(payload) {
		try {
			await apiClient().post('/Lead/TransferLeads', payload);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async updateMultipleLeads(payload) {
		try {
			const { data, status } = await apiClient().post(
				'/Lead/UpdateLeadType',
				payload,
			);
			if (status !== 200 || data !== true) {
				throw new Error('Contacts were not updated');
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async sendLeadToBlink(payload) {
		try {
			const response = await apiClient().post('/Blink/SendLead', payload);
			if (response.data.emailStatus === false) {
				if (response.data.publicId) {
					return Promise.reject(
						'Something went wrong, we were able to create an application, but please go to Blink+ to send the application to this contact.',
					);
				} else {
					return Promise.reject(
						'Something went wrong, please go to Blink+ to create an application for this contact.',
					);
				}
			} else {
				return Promise.resolve(
					'Something went wrong, there was an error sending to Blink+.',
				);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async sendLeadToVendor(payload) {
		try {
			const response = await apiClient().post(
				'/Communication/SendVendorLinkEmail',
				payload,
			);
			if (!!response.data) return Promise.resolve(response.data);
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async updateActiveStatus(payload) {
		try {
			const { status } = await apiClient().post(
				'/Lead/UpdateContactStatus',
				payload,
			);
			if (status !== 200) {
				throw new Error('Contacts were not updated');
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
};
